'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import React, { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { DesktopHeader } from '@/app/_components/DesktopHeader';
import { FloatingBnbController } from '@/app/_components/FloatingBnbController';
import { GlobalModal } from '@/app/_components/GlobalModal';
import { GlobalNavigation } from '@/app/_components/GlobalNavigation';
import { ToastProvider } from '@/app/_components/ToastProvider';
import { ToastSearchParamsController } from '@/app/_components/ToastSearchParamsController';
import { SELECTOR } from '@/constants';
import { useGlobalStore } from '@/stores/GlobalStore';
import { findRoute } from '@/utils';
import { HIDE_DESKTOP_HEADER_PATHNAMES } from './constants';
import { ResponsiveDialogContainer } from './ResponsiveDialogContainer';

// SSR 비활성화로 App Router 마운트 이슈 해결
const NaverCtsTracker = dynamic(() => import('./NaverCtsTracker').then(mod => ({
  default: mod.NaverCtsTracker
})), {
  ssr: false
});
interface GlobalLayoutProps {
  children: React.ReactNode;
}
const OptionalGlobalFeatureBoundary = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <ErrorBoundary fallback={null}>{children}</ErrorBoundary>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const GlobalLayout = t0 => {
  const $ = _c(33);
  const {
    children
  } = t0;
  const pathname = usePathname();
  const currentRoute = findRoute(pathname);
  const hideBottomNav = useGlobalStore(_temp);
  let t1;
  if ($[0] !== pathname) {
    t1 = HIDE_DESKTOP_HEADER_PATHNAMES.includes(pathname ?? "");
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const showDesktopHeader = !t1;
  const hasBottomNav = !hideBottomNav && (currentRoute?.hasBottomNav ?? true);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <a href={`#${SELECTOR.MAIN}`} className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[9999] focus:rounded-small focus:bg-Gray-black focus:px-16 focus:py-8 focus:font-600 focus:text-Gray-white focus:typo-body4">본문 바로가기</a>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== showDesktopHeader) {
    t3 = showDesktopHeader && <DesktopHeader />;
    $[3] = showDesktopHeader;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = showDesktopHeader && "has-[article[data-header-offset-page]]:min-h-[calc(100dvh-4.5rem-1px)] mobile-tablet:has-[article[data-header-offset-page]]:min-h-dvh";
  let t5;
  if ($[5] !== t4) {
    t5 = cn("relative flex min-h-dvh max-w-full flex-col overflow-x-clip bg-Gray-white", t4);
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = SELECTOR;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="sticky top-0 z-20 hidden bg-Gray-white mobile-tablet:block" id={SELECTOR.PAGE_NAV_WRAPPER} />;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <OptionalGlobalFeatureBoundary><ResponsiveDialogContainer /></OptionalGlobalFeatureBoundary>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const T0 = ToastProvider;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ToastSearchParamsController />;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const t10 = cn("relative flex flex-1 flex-col", {
    "mobile-tablet:pb-[calc(70px+var(--safe-area-bottom,0px))]": hasBottomNav
  });
  let t11;
  if ($[10] !== children) {
    t11 = <Suspense fallback={null}>{children}</Suspense>;
    $[10] = children;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Suspense fallback={null}><OptionalGlobalFeatureBoundary><NaverCtsTracker /></OptionalGlobalFeatureBoundary></Suspense>;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== t10 || $[14] !== t11) {
    t13 = <main className={t10} id={SELECTOR.MAIN}>{t11}{t12}</main>;
    $[13] = t10;
    $[14] = t11;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== T0 || $[17] !== t13 || $[18] !== t9) {
    t14 = <T0>{t9}{t13}</T0>;
    $[16] = T0;
    $[17] = t13;
    $[18] = t9;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  let t16;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <GlobalModal />;
    t16 = <GlobalNavigation />;
    $[20] = t15;
    $[21] = t16;
  } else {
    t15 = $[20];
    t16 = $[21];
  }
  let t17;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <OptionalGlobalFeatureBoundary><FloatingBnbController /></OptionalGlobalFeatureBoundary>;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] !== t14 || $[24] !== t5 || $[25] !== t6.MAIN_WRAPPER || $[26] !== t7 || $[27] !== t8) {
    t18 = <div className={t5} id={t6.MAIN_WRAPPER}>{t7}{t8}{t14}{t15}{t16}{t17}</div>;
    $[23] = t14;
    $[24] = t5;
    $[25] = t6.MAIN_WRAPPER;
    $[26] = t7;
    $[27] = t8;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t18 || $[30] !== t2 || $[31] !== t3) {
    t19 = <>{t2}{t3}{t18}</>;
    $[29] = t18;
    $[30] = t2;
    $[31] = t3;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  return t19;
};
function _temp(state) {
  return state.hideBottomNav;
}